import { isBlurhashValid } from 'blurhash'
import { mapState } from 'pinia'
import { defineAsyncComponent } from 'vue'

import BlurhashCanvas from 'src/components/blurhash_canvas/blurhash_canvas.vue'
import Popover from 'src/components/popover/popover.vue'

import { useInstanceStore } from 'src/stores/instance.js'
import { useInstanceCapabilitiesStore } from 'src/stores/instance_capabilities.js'
import { useMediaViewerStore } from 'src/stores/media_viewer'
import { useMergedConfigStore } from 'src/stores/merged_config.js'

import {
  fileExtension,
  fileNameFromUrl,
} from 'src/services/file_name/file_name.js'

// with its metadata reader, loaded for the first audio file
const AudioPlayer = defineAsyncComponent(
  () => import('src/components/audio_player/audio_player.vue'),
)

const Attachment = {
  props: [
    'attachment',
    'compact',
    'description',
    'hideDescription',
    'nsfw',
    'size',
    'setMedia',
    'remove',
    'shiftUp',
    'shiftDn',
    'edit',
    // reveal state owned by the gallery (null when standalone)
    'revealed',
  ],
  emits: ['play', 'pause', 'naturalSizeLoad', 'update:revealed'],
  data() {
    return {
      localDescription: this.description || this.attachment.description,
      customCensorImage:
        useInstanceStore().instanceIdentity.nsfwCensorImage || null,
      preloadImage: useMergedConfigStore().mergedConfig.preloadImage,
      loading: false,
      img: this.attachment.type === 'image' && document.createElement('img'),
      showHidden: false,
      flashLoaded: false,
    }
  },
  components: {
    BlurhashCanvas,
    AudioPlayer,
    Flash: defineAsyncComponent(() => import('src/components/flash/flash.vue')),

    VideoAttachment: defineAsyncComponent(
      () => import('src/components/video_attachment/video_attachment.vue'),
    ),
    Popover,
  },
  computed: {
    classNames() {
      return [
        {
          '-loading': this.loading,
          '-nsfw-placeholder': this.hidden,
          '-editable': this.edit !== undefined,
          '-compact': this.compact,
        },
        '-type-' + this.attachment.type,
        this.size && '-size-' + this.size,
        `-${this.useContainFit ? 'contain' : 'cover'}-fit`,
      ]
    },
    usePlaceholder() {
      return this.size === 'hide'
    },
    useContainFit() {
      return this.mergedConfig.useContainFit
    },
    placeholderName() {
      if (this.attachment.description === '' || !this.attachment.description) {
        return this.attachment.type.toUpperCase()
      }
      return this.attachment.description
    },
    // Files have nothing to show, so the row says what it is instead
    fileName() {
      return (
        this.localDescription ||
        fileNameFromUrl(this.attachment.url, this.$t('status.attachment_file'))
      )
    },
    fileType() {
      return fileExtension(this.attachment.url)
    },
    placeholderIconClass() {
      if (this.attachment.type === 'image') return 'image'
      if (this.attachment.type === 'video') return 'video'
      if (this.attachment.type === 'audio') return 'music'
      return 'file'
    },
    referrerpolicy() {
      return useInstanceCapabilitiesStore().mediaProxyAvailable
        ? ''
        : 'no-referrer'
    },
    // read live so a settings change re-hides or reveals at once
    hideNsfwLocal() {
      return this.mergedConfig.hideNsfw
    },
    hidden() {
      const revealed =
        this.revealed === null || this.revealed === undefined
          ? this.showHidden
          : this.revealed
      return this.nsfw && this.hideNsfwLocal && !revealed
    },
    // What stands in for a hidden attachment: a decoded blurhash needs no
    // network, a blurred thumbnail does, and the instance's own censor
    // image is kept only as a last resort.
    placeholderKind() {
      if (isBlurhashValid(this.attachment.blurhash || '').result) {
        return 'blurhash'
      }
      const hasThumbnail =
        this.attachment.type === 'image' &&
        (this.attachment.large_thumb_url || this.attachment.url)
      if (this.preloadImage && hasThumbnail) return 'blur'
      if (this.customCensorImage) return 'image'
      return 'plain'
    },
    isEmpty() {
      return this.attachment.type === 'html' && !this.attachment.oembed
    },
    useModal() {
      let modalTypes = []
      switch (this.size) {
        case 'hide':
        case 'small':
          modalTypes = ['image', 'video', 'audio', 'flash']
          break
        default:
          modalTypes = this.mergedConfig.playVideosInModal
            ? ['image', 'video', 'flash']
            : ['image']
          break
      }
      return modalTypes.includes(this.attachment.type)
    },
    videoTag() {
      return this.useModal ? 'button' : 'span'
    },
    ...mapState(useMergedConfigStore, ['mergedConfig']),
  },
  watch: {
    'attachment.description'(newVal) {
      this.localDescription = newVal
    },
    localDescription(newVal) {
      this.onEdit(newVal)
    },
  },
  methods: {
    linkClicked({ target }) {
      if (target.tagName === 'A') {
        window.open(target.href, '_blank')
      }
    },
    openModal() {
      if (this.useModal) {
        this.$emit('setMedia')
        useMediaViewerStore().setCurrentMedia(this.attachment)
      } else if (this.attachment.type === 'unknown') {
        window.open(this.attachment.url)
      }
    },
    openModalForce() {
      this.$emit('setMedia')
      useMediaViewerStore().setCurrentMedia(this.attachment)
    },
    onEdit(event) {
      this.edit?.(this.attachment, event)
    },
    onRemove() {
      this.remove?.(this.attachment)
    },
    onShiftUp() {
      this.shiftUp?.(this.attachment)
    },
    onShiftDn() {
      this.shiftDn?.(this.attachment)
    },
    stopFlash() {
      this.$refs.flash.closePlayer()
    },
    setFlashLoaded(event) {
      this.flashLoaded = event
    },
    toggleHidden(event) {
      if (
        this.mergedConfig.useOneClickNsfw &&
        this.hidden &&
        (this.attachment.type !== 'video' ||
          this.mergedConfig.playVideosInModal)
      ) {
        this.openModal(event)
        return
      }
      // Load the full image before revealing when it was not preloaded;
      // hiding never needs to wait.
      if (this.img && !this.preloadImage && this.hidden) {
        if (this.img.onload) {
          this.img.onload()
        } else {
          this.loading = true
          this.img.src = this.attachment.url
          this.img.onload = () => {
            this.loading = false
            this.setRevealed(true)
          }
          this.img.onerror = () => {
            this.loading = false
            this.setRevealed(true)
          }
        }
      } else {
        this.setRevealed(this.hidden)
      }
    },
    setRevealed(value) {
      if (this.revealed === null || this.revealed === undefined) {
        this.showHidden = value
      } else {
        this.$emit('update:revealed', value)
      }
    },
    onImageLoad(image) {
      const width = image.naturalWidth
      const height = image.naturalHeight
      this.$emit('naturalSizeLoad', { id: this.attachment.id, width, height })
    },
  },
}

export default Attachment
